<template>
  <div :class="['relative group', wrapperClass]">
    <!-- Trigger slot (icon, button, text, etc.) -->
    <slot name="trigger">
      <Info class="w-4 h-4 cursor-help" :class="triggerClass" />
    </slot>

    <!-- Tooltip content -->
    <div
      :class="[
        'absolute px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-10',
        positionClasses,
        contentClass,
        { 'whitespace-nowrap': !wrapText }
      ]"
    >
      <!-- Custom content or text -->
      <slot>{{ text }}</slot>

      <!-- Arrow -->
      <div
        v-if="showArrow"
        :class="[
          'absolute border-4 border-transparent',
          arrowClasses
        ]"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Info } from 'lucide-vue-next'

interface Props {
  text?: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  showArrow?: boolean
  wrapText?: boolean
  triggerClass?: string
  contentClass?: string
  maxWidth?: string
  wrapperClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  position: 'left',
  showArrow: true,
  wrapText: false,
  triggerClass: 'text-gray-400 dark:text-gray-500',
  contentClass: '',
  maxWidth: '',
  wrapperClass: 'inline'
})

const positionClasses = computed(() => {
  switch (props.position) {
    case 'top':
      return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'
    case 'bottom':
      return 'top-full left-1/2 transform -translate-x-1/2 mt-2'
    case 'left':
      return 'right-full top-1/2 transform -translate-y-1/2 mr-2'
    case 'right':
      return 'left-full top-1/2 transform -translate-y-1/2 ml-2'
    case 'top-left':
      return 'bottom-full right-0 mb-2'
    case 'top-right':
      return 'bottom-full left-0 mb-2'
    case 'bottom-left':
      return 'top-full right-0 mt-2'
    case 'bottom-right':
      return 'top-full left-0 mt-2'
    default:
      return 'right-full top-1/2 transform -translate-y-1/2 mr-2'
  }
})

const arrowClasses = computed(() => {
  switch (props.position) {
    case 'top':
      return 'top-full left-1/2 transform -translate-x-1/2 -mt-1 border-t-gray-900 dark:border-t-gray-700'
    case 'bottom':
      return 'bottom-full left-1/2 transform -translate-x-1/2 -mb-1 border-b-gray-900 dark:border-b-gray-700'
    case 'left':
      return 'right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700'
    case 'right':
      return 'left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-700'
    case 'top-left':
      return 'top-full right-2 -mt-1 border-t-gray-900 dark:border-t-gray-700'
    case 'top-right':
      return 'top-full left-2 -mt-1 border-t-gray-900 dark:border-t-gray-700'
    case 'bottom-left':
      return 'bottom-full right-2 -mb-1 border-b-gray-900 dark:border-b-gray-700'
    case 'bottom-right':
      return 'bottom-full left-2 -mb-1 border-b-gray-900 dark:border-b-gray-700'
    default:
      return 'right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700'
  }
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
